/**
 * The pieces every /field/pt screen draws the same way (PT-3).
 *
 * THE FRAME IS THE CASING FIELD TREE'S OWN (FieldJobsScreen, FieldMoreScreen):
 * the one global <Header />, a back arrow and an <h1>, then the page. A PT
 * technician lands in that tree (App.tsx `isFieldWorker`, neither trucking nor
 * rentals), which has no bottom bar — so neither does this. No floating bar,
 * no sticky footer: every action is a full-width button in the flow.
 *
 * The card, button and load-state idiom is the downhole field screens'
 * (pages/downhole/field/FieldBits.tsx), IMPORTED and not edited, so the two
 * verticals' field screens stay one shape.
 */
import React from 'react';
import { ArrowLeft } from 'lucide-react';
import Header from '../../../components/Header';
import { useBack } from '../../../hooks/useBack';
import type { PtTest } from '../../../types/pressure-testing';
import type { PtStatusSkin } from '../../../lib/pressure-testing';

export {
  FieldCard as PtFieldCard,
  FieldEmpty as PtFieldEmpty,
  FieldQueryState as PtFieldQueryState,
  FieldSectionTitle as PtFieldSectionTitle,
  FIELD_PRIMARY as PT_FIELD_PRIMARY,
  FIELD_SECONDARY as PT_FIELD_SECONDARY,
} from '../../downhole/field/FieldBits';

export function PtFieldPage({
  title,
  backTo,
  testId,
  children,
}: {
  title: string;
  /** Where Back goes when there is no history to return to. */
  backTo: string;
  testId: string;
  children: React.ReactNode;
}) {
  const goBack = useBack(backTo);
  return (
    <div data-testid={testId} className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="mx-auto max-w-[1920px]">
        <Header />
        <div className="flex items-center gap-3 border-b border-fieldiq-border px-4 py-3">
          <button type="button" onClick={goBack} aria-label="Back" className="text-fieldiq-text">
            <ArrowLeft size={26} />
          </button>
          <h1 className="min-w-0 truncate text-xl font-bold text-white">{title}</h1>
        </div>
      </div>
      <div className="mx-auto max-w-3xl px-4 py-4">{children}</div>
    </div>
  );
}

/** A saturated badge, dark ink on the fill — the register idiom. */
export function PtFieldBadge({ skin, testId }: { skin: PtStatusSkin; testId?: string }) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

/**
 * Where a test stands, for the checklist. The RESULT is the API's; the two
 * in-between states read the server's own hold stamps. Five states over the
 * five signal hues, each meaning one thing:
 *
 *   pending   slate  no hold started, no verdict — a gap, not a state of the well
 *   holding   amber  started_at stamped, ended_at not: the clock is running
 *   stopped   blue   both stamps, no verdict yet: waiting on the end pressure
 *   pass      green
 *   fail      red
 *
 * An unknown result claims nothing: the slate `—`, never a real state.
 */
export type PtTestState = 'pending' | 'holding' | 'stopped' | 'pass' | 'fail';

export const PT_TEST_STATE_SKIN: Record<PtTestState, PtStatusSkin> = {
  pending: { label: 'Pending', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  holding: { label: 'Holding', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  stopped: { label: 'Hold stopped', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  pass: { label: 'Pass', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  fail: { label: 'Fail', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};

const UNKNOWN_STATE: PtStatusSkin = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };

export function ptTestState(t: Pick<PtTest, 'result' | 'started_at' | 'ended_at'>): PtTestState | null {
  if (t.result === 'pass' || t.result === 'fail') return t.result;
  if (t.result !== 'pending') return null;
  if (t.started_at && !t.ended_at) return 'holding';
  if (t.started_at && t.ended_at) return 'stopped';
  return 'pending';
}

export function ptTestStateSkin(t: Pick<PtTest, 'result' | 'started_at' | 'ended_at'>): PtStatusSkin {
  const s = ptTestState(t);
  return s ? PT_TEST_STATE_SKIN[s] : UNKNOWN_STATE;
}

/** An API refusal, inline where the action was. Theme ink on a red wash; the wash and the edge carry the hue. */
export function PtRefusal({ message, testId, children }: { message: string | null | undefined; testId?: string; children?: React.ReactNode }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      data-testid={testId}
      className="rounded-fi-control border border-fi-signal-red/40 border-l-4 border-l-fi-red bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text"
    >
      <p className="font-semibold">{message}</p>
      {children}
    </div>
  );
}
